import { getWorkflowStatus, getPortalLabel, PIPELINE_STEPS, type WorkflowStatus } from "@/lib/workflowStatus";
import { Link } from "react-router-dom";

/* ─────────────────────────────── Types ─────────────────────────────── */
export type PortalJob = {
  id: string;
  title: string;
  job_type: string;
  status: string;
  workflow_status?: string | null;
  shoot_date: string | null;
  location?: string | null;
  photo_selection_status?: string | null;
  photo_selection_submitted_at?: string | null;
  delivery_status?: string | null;
  delivered_at?: string | null;
  editing_started_at?: string | null;
  completed_at?: string | null;
};

export type JobStep = {
  id: string;
  job_id: string;
  step_key: string;
  title: string;
  status: string;
  sort_order: number;
  due_date: string | null;
  completed_at: string | null;
};

export type PortalMediaPackage = {
  id: string;
  job_id?: string | null;
  package_type: string;
  title: string;
  cover_url: string | null;
  video_url?: string | null;
  download_url?: string | null;
  public_token: string;
  is_published?: boolean;
};

export type PortalJobDelivery = {
  id: string;
  job_id: string;
  title: string;
  delivery_type: string;
  file_url: string | null;
  is_active: boolean;
};

export type PortalSignatureBook = {
  id: string;
  client_id: string;
  status: string;
};

interface Props {
  jobs: PortalJob[];
  steps: JobStep[];
  mediaPackages?: PortalMediaPackage[];
  deliveries?: PortalJobDelivery[];
  hasSignatureBook?: boolean;
  signatureBookToken?: string;
  portalToken: string;
  brandColor?: string;
  features?: {
    photo_selection?: boolean;
    final_deliveries?: boolean;
    dvd_online?: boolean;
    signature_book?: boolean;
  };
}

/* ─────────────────────────────── Helpers ────────────────────────────── */
function fmtDate(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function fmtDateShort(d: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* ─── Client-facing timeline stages ─── */
type ClientStage = {
  id: WorkflowStatus | "waiting_client";
  label: string;
  portalDesc: string;
  icon: string;
};

const CLIENT_STAGES: ClientStage[] = [
  {
    id: "scheduled",
    label: "Booked",
    portalDesc: "Your session is confirmed and on the calendar",
    icon: "ri-calendar-check-line",
  },
  {
    id: "shooting",
    label: "Session",
    portalDesc: "Your photo/video session day",
    icon: "ri-camera-3-line",
  },
  {
    id: "editing",
    label: "Editing",
    portalDesc: "Your photos are being professionally edited",
    icon: "ri-magic-line",
  },
  {
    id: "waiting_client",
    label: "Your Turn",
    portalDesc: "Please review and select your favourite photos",
    icon: "ri-hand-heart-line",
  },
  {
    id: "rendering",
    label: "Finishing",
    portalDesc: "Final touches and preparing your files",
    icon: "ri-settings-3-line",
  },
  {
    id: "delivered",
    label: "Delivered",
    portalDesc: "Your files are ready to download",
    icon: "ri-gift-line",
  },
  {
    id: "completed",
    label: "Complete",
    portalDesc: "Your project is fully complete",
    icon: "ri-award-line",
  },
];

function getClientStageIndex(wfStatus: string): number {
  const order: Record<string, number> = {
    scheduled: 0,
    confirmed: 0,
    shooting: 1,
    editing: 2,
    waiting_client: 3,
    rendering: 4,
    delivered: 5,
    completed: 6,
  };
  return order[wfStatus] ?? 0;
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function JobTimelineSection({
  jobs,
  steps,
  mediaPackages = [],
  deliveries = [],
  hasSignatureBook = false,
  portalToken,
  brandColor = "#C8A96E",
  features = {},
}: Props) {
  return (
    <section>
      <h2 className="text-[#0f172a] font-bold text-base flex items-center gap-2 mb-4">
        <span className="w-5 h-5 flex items-center justify-center" style={{ color: brandColor }}>
          <i className="ri-camera-3-line"></i>
        </span>
        Your Event
      </h2>

      <div className="space-y-5">
        {jobs.map((job) => {
          const jobSteps = steps.filter((s) => s.job_id === job.id);
          const jobDeliveries = deliveries.filter((d) => d.job_id === job.id && d.is_active);
          const jobMediaPkgs = mediaPackages.filter((m) => m.job_id === job.id && m.is_published);

          const wfStatus = job.workflow_status || job.status || "scheduled";
          const wfCfg = getWorkflowStatus(wfStatus);
          const portalLabel = getPortalLabel(wfStatus);
          const isCancelled = wfStatus === "cancelled";
          const isDelivered = wfStatus === "delivered" || wfStatus === "completed";
          const stageIdx = getClientStageIndex(wfStatus);

          const completedSteps = jobSteps.filter((s) => s.status === "completed").length;
          const totalSteps = jobSteps.length;

          /* Quick-access modules */
          const showPhotoSelection =
            features.photo_selection &&
            (job.photo_selection_status === "in_progress" ||
              job.photo_selection_status === "waiting" ||
              wfStatus === "waiting_client");
          const hasSubmittedSelection =
            job.photo_selection_status === "submitted" ||
            job.photo_selection_status === "reviewed";
          const showDeliveries = features.final_deliveries && jobDeliveries.length > 0;
          const showMedia = features.dvd_online && jobMediaPkgs.length > 0;
          const showSigBook = features.signature_book && hasSignatureBook;

          return (
            <div
              key={job.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            >
              {/* ── Job hero header ── */}
              <div
                className="px-5 pt-5 pb-4"
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                {/* Title + type */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h3 className="text-[#0f172a] font-bold text-base leading-tight truncate">{job.title}</h3>
                    <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1.5 capitalize">
                      <i className="ri-camera-lens-line text-[10px]"></i>
                      {job.job_type}
                    </p>
                  </div>
                  {/* Status pill */}
                  {!isCancelled && (
                    <span
                      className={`text-[11px] font-semibold px-3 py-1 rounded-full border whitespace-nowrap inline-flex items-center gap-1.5 flex-shrink-0 ${wfCfg.bg} ${wfCfg.border} ${wfCfg.color}`}
                    >
                      <i className={`${wfCfg.icon} text-[11px]`}></i>
                      {wfCfg.label}
                    </span>
                  )}
                </div>

                {/* Date + location row */}
                <div className="flex items-center gap-4 flex-wrap mb-3">
                  {job.shoot_date && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <i className="ri-calendar-event-line text-slate-300 text-xs"></i>
                      {fmtDate(job.shoot_date)}
                    </div>
                  )}
                  {job.location && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <i className="ri-map-pin-line text-slate-300 text-xs"></i>
                      {job.location}
                    </div>
                  )}
                </div>

                {/* Status message banner */}
                {!isCancelled ? (
                  <div
                    className="flex items-start gap-3 rounded-xl px-4 py-3 mb-4"
                    style={{ backgroundColor: `${brandColor}12`, border: `1px solid ${brandColor}30` }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${brandColor}20` }}
                    >
                      <i className={`${wfCfg.icon} text-sm`} style={{ color: brandColor }}></i>
                    </div>
                    <div>
                      <p className="text-[#0f172a] font-semibold text-sm">{portalLabel}</p>
                      {wfStatus === "waiting_client" && (
                        <p className="text-slate-500 text-xs mt-0.5">
                          Your photographer is waiting for you to review and select your favourite photos.
                        </p>
                      )}
                      {wfStatus === "editing" && (
                        <p className="text-slate-500 text-xs mt-0.5">
                          Our team is working on your photos. We&apos;ll notify you when they&apos;re ready.
                        </p>
                      )}
                      {wfStatus === "delivered" && (
                        <p className="text-slate-500 text-xs mt-0.5">
                          Your files are ready! Download them from the deliveries section below.
                        </p>
                      )}
                      {wfStatus === "rendering" && (
                        <p className="text-slate-500 text-xs mt-0.5">
                          We&apos;re finalizing your files. Almost there!
                        </p>
                      )}
                      {wfStatus === "completed" && (
                        <p className="text-slate-500 text-xs mt-0.5">
                          Your project is fully complete. Thank you for choosing us!
                        </p>
                      )}
                      {/* Key timestamps */}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {job.editing_started_at && (
                          <span className="text-slate-400 text-[10px] flex items-center gap-1">
                            <i className="ri-edit-2-line text-[10px]"></i>
                            Editing since {fmtDateShort(job.editing_started_at)}
                          </span>
                        )}
                        {job.delivered_at && (
                          <span className="text-emerald-500 text-[10px] flex items-center gap-1 font-medium">
                            <i className="ri-send-plane-fill text-[10px]"></i>
                            Delivered {fmtDateShort(job.delivered_at)}
                          </span>
                        )}
                        {job.completed_at && (
                          <span className="text-emerald-600 text-[10px] flex items-center gap-1 font-medium">
                            <i className="ri-checkbox-circle-fill text-[10px]"></i>
                            Completed {fmtDateShort(job.completed_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                    <i className="ri-close-circle-line text-red-500"></i>
                    <p className="text-red-600 text-sm font-medium">This session was cancelled.</p>
                  </div>
                )}

                {/* ── Visual timeline ── */}
                {!isCancelled && (
                  <div className="mt-2">
                    {/* Desktop horizontal timeline */}
                    <div className="hidden sm:flex items-start gap-0">
                      {CLIENT_STAGES.map((stage, idx) => {
                        const isPast = idx < stageIdx;
                        const isActive = idx === stageIdx;
                        const isFuture = idx > stageIdx;
                        const isLast = idx === CLIENT_STAGES.length - 1;

                        return (
                          <div key={stage.id} className="flex-1 flex flex-col items-center relative">
                            {/* Connecting line */}
                            {!isLast && (
                              <div
                                className="absolute top-[15px] left-1/2 right-0 h-0.5 z-0"
                                style={{ backgroundColor: isPast || isActive ? brandColor : "#e2e8f0" }}
                              ></div>
                            )}
                            {/* Node */}
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center z-10 mb-2 transition-all flex-shrink-0"
                              style={{
                                backgroundColor: isPast
                                  ? "#10b981"
                                  : isActive
                                    ? brandColor
                                    : "#f1f5f9",
                                border: isActive ? `2px solid ${brandColor}` : isPast ? "2px solid #10b981" : "2px solid #e2e8f0",
                              }}
                            >
                              {isPast ? (
                                <i className="ri-check-line text-white text-xs"></i>
                              ) : (
                                <i
                                  className={`${stage.icon} text-xs`}
                                  style={{ color: isActive ? "#fff" : "#cbd5e1" }}
                                ></i>
                              )}
                            </div>
                            {/* Label */}
                            <p
                              className="text-center text-[10px] font-semibold leading-tight whitespace-nowrap"
                              style={{
                                color: isActive ? brandColor : isPast ? "#10b981" : "#94a3b8",
                              }}
                            >
                              {stage.label}
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Mobile: compact progress bar */}
                    <div className="sm:hidden">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-slate-400 text-xs">Progress</span>
                        <span className="text-xs font-semibold" style={{ color: brandColor }}>
                          {CLIENT_STAGES[stageIdx]?.label}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${Math.round(((stageIdx + 1) / CLIENT_STAGES.length) * 100)}%`,
                            backgroundColor: isDelivered ? "#10b981" : brandColor,
                          }}
                        />
                      </div>
                      <p className="text-slate-400 text-[10px] mt-1">
                        Stage {stageIdx + 1} of {CLIENT_STAGES.length}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Quick-access modules ── */}
              {(showPhotoSelection || hasSubmittedSelection || showDeliveries || showMedia || showSigBook) && (
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-3 font-semibold">Quick Access</p>
                  <div className="grid grid-cols-1 gap-2">

                    {/* Photo Selection */}
                    {(showPhotoSelection || hasSubmittedSelection) && (
                      <a
                        href="#photo-selection"
                        className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 hover:border-slate-300 p-3 transition-all group cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("photo-selection")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${brandColor}18` }}
                        >
                          <i className="ri-image-2-line text-base" style={{ color: brandColor }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0f172a] font-semibold text-sm">Photo Selection</p>
                          {hasSubmittedSelection ? (
                            <p className="text-amber-600 text-xs">
                              {job.photo_selection_status === "reviewed"
                                ? "Reviewed by photographer ✓"
                                : "Submitted — awaiting review"}
                            </p>
                          ) : (
                            <p className="text-slate-400 text-xs">Select your favourite photos</p>
                          )}
                        </div>
                        <div
                          className="w-7 h-7 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          style={{ backgroundColor: `${brandColor}18` }}
                        >
                          <i className="ri-arrow-right-s-line text-sm" style={{ color: brandColor }}></i>
                        </div>
                        {wfStatus === "waiting_client" && !hasSubmittedSelection && (
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 text-white"
                            style={{ backgroundColor: brandColor }}
                          >
                            Action required
                          </span>
                        )}
                      </a>
                    )}

                    {/* Deliveries */}
                    {showDeliveries && (
                      <a
                        href="#deliveries"
                        className="flex items-center gap-3 bg-white rounded-xl border border-emerald-200 hover:border-emerald-300 p-3 transition-all group cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("deliveries")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-50">
                          <i className="ri-download-cloud-line text-base text-emerald-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0f172a] font-semibold text-sm">Downloads Ready</p>
                          <p className="text-emerald-600 text-xs font-medium">
                            {jobDeliveries.length} file{jobDeliveries.length !== 1 ? "s" : ""} available
                          </p>
                        </div>
                        <i className="ri-arrow-right-s-line text-slate-300 group-hover:text-emerald-600 transition-colors text-sm flex-shrink-0"></i>
                      </a>
                    )}

                    {/* Media Packages */}
                    {showMedia && jobMediaPkgs.map((pkg) => (
                      <a
                        key={pkg.id}
                        href={`/media/${pkg.public_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white rounded-xl border border-violet-200 hover:border-violet-300 p-3 transition-all group cursor-pointer"
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-violet-50 overflow-hidden">
                          {pkg.cover_url ? (
                            <img src={pkg.cover_url} alt={pkg.title} className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-disc-line text-base text-violet-600"></i>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0f172a] font-semibold text-sm truncate">{pkg.title}</p>
                          <p className="text-violet-600 text-xs capitalize">{pkg.package_type.replace(/_/g, " ")}</p>
                        </div>
                        <i className="ri-external-link-line text-slate-300 group-hover:text-violet-600 transition-colors text-sm flex-shrink-0"></i>
                      </a>
                    ))}

                    {/* Signature Book */}
                    {showSigBook && (
                      <Link
                        to={`/portal/${portalToken}/signature-book`}
                        className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 hover:border-slate-300 p-3 transition-all group cursor-pointer"
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-50">
                          <i className="ri-book-open-line text-base text-amber-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0f172a] font-semibold text-sm">Signature Book</p>
                          <p className="text-slate-400 text-xs">View guest messages & download PDF</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-slate-300 group-hover:text-amber-600 transition-colors text-sm flex-shrink-0"></i>
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* ── Workflow steps (simplified for client) ── */}
              {jobSteps.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-3 font-semibold flex items-center justify-between">
                    <span>Production Steps</span>
                    {totalSteps > 0 && (
                      <span style={{ color: brandColor }} className="font-bold">
                        {completedSteps}/{totalSteps}
                      </span>
                    )}
                  </p>

                  {/* Progress bar */}
                  {totalSteps > 0 && (
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.round((completedSteps / totalSteps) * 100)}%`,
                          backgroundColor: completedSteps === totalSteps ? "#10b981" : brandColor,
                        }}
                      />
                    </div>
                  )}

                  {/* Steps list */}
                  <div className="relative">
                    <div className="absolute left-[13px] top-3 bottom-3 w-px bg-slate-100"></div>
                    <div className="space-y-2.5">
                      {jobSteps.map((step, idx) => {
                        const isDone = step.status === "completed";
                        const isActive = step.status === "in_progress";
                        const isBlocked = step.status === "blocked";

                        return (
                          <div key={step.id} className="flex items-start gap-3 relative">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 text-xs font-semibold ${
                                isDone
                                  ? "bg-emerald-100 text-emerald-600 ring-1 ring-emerald-300"
                                  : isBlocked
                                    ? "bg-red-100 text-red-500 ring-1 ring-red-200"
                                    : isActive
                                      ? "ring-2 ring-offset-1 text-white"
                                      : "bg-slate-100 text-slate-300"
                              }`}
                              style={isActive ? { backgroundColor: brandColor, ringColor: brandColor } : {}}
                            >
                              {isDone ? (
                                <i className="ri-check-line text-xs"></i>
                              ) : isActive ? (
                                <i className="ri-loader-4-line text-xs animate-spin"></i>
                              ) : isBlocked ? (
                                <i className="ri-pause-line text-xs"></i>
                              ) : (
                                <span className="text-[10px]">{idx + 1}</span>
                              )}
                            </div>

                            <div className="flex-1 min-w-0 py-0.5">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm ${
                                  isDone ? "text-slate-300 line-through" :
                                  isActive ? "text-[#0f172a] font-semibold" :
                                  "text-slate-500"
                                }`}>
                                  {step.title}
                                </p>
                                {isActive && (
                                  <span
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 text-white"
                                    style={{ backgroundColor: brandColor }}
                                  >
                                    Now
                                  </span>
                                )}
                                {isDone && step.completed_at && (
                                  <span className="text-[10px] text-emerald-400 whitespace-nowrap flex-shrink-0">
                                    ✓ {fmtDateShort(step.completed_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}